// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { ReactNode } from "react"
import { ChevronRight } from "lucide-react"
import { useEvolutionCycle } from "@/queries/queries.evolutionCycles"
import { useForge } from "@/queries/queries.forges"
import { selectBudgetInfo, selectCycleConfig } from "@/lib/cycle-config"
import { parseEvaluationProtocol } from "@/lib/evaluation-protocol"
import { formatCurrency } from "@/utils"

/**
 * `SetupPanel` — collapsible "frozen at launch" SETUP region (PRD
 * §Modules `SETUP` panel; issue `fe-setup-panel`, US §4/§5/§6/§7/§8/§20).
 * Mirrors the "Cycle Dashboard 2A" mock 1:1: collapsed by default with a
 * compact one-line digest in the summary bar, and a 2×2 grid of
 * sub-panels (FORGE / CYCLE CONFIG / EVALUATION / EVOLVER) fed by the
 * already-fetched cycle detail + forge detail queries and the data-layer
 * selectors. EVALUATION is its own dedicated sub-panel: the protocol is
 * broken into labelled rows (trials / aggregation / timeout / dataset /
 * metrics) rather than one crammed line.
 *
 * The dataset row shows the train/test example COUNTS only (ADR-0070,
 * amending ADR-0007): the counts are extracted server-side into typed
 * `cycle.train_size` / `cycle.test_size` columns. The dataset *content*
 * (sample question/answer) stays stripped at source and never reaches the
 * DOM (the enduring INV-7 privacy line — pinned by the INV-7 test below).
 */
export interface SetupPanelProps {
  cycleId: string
  /** Tests render the panel pre-expanded; the product default is collapsed (mock parity). */
  defaultOpen?: boolean
}

const DASH = "—"

const formatScore = (value: number | null | undefined): string =>
  typeof value === "number" ? value.toFixed(3) : DASH

const formatDate = (value: string | null | undefined): string =>
  value ? value.slice(0, 10) : DASH

const formatNumber = (value: number | null | undefined): string =>
  typeof value === "number" ? `${value}` : DASH

/**
 * Display a self-described config field (ADR-0069). The evolver already
 * stringified `value`; `kind` lets us polish it (a ratio renders to two
 * decimals). An empty value degrades to "—" (CORR-02).
 */
const formatFieldValue = (field: EvolutionCycleConfigField): string => {
  const raw = field.value?.trim()
  if (!raw) return DASH
  if (field.kind === "ratio") {
    const n = Number(raw)
    return Number.isFinite(n) ? n.toFixed(2) : raw
  }
  return raw
}

/**
 * Dataset size row (ADR-0070) — `<train> train / <test> test`. Both counts
 * absent ⇒ a single em-dash (CORR-02, never a fabricated 0); a lone absent
 * count shows "—" on its side so a present count is never hidden.
 */
const formatDatasetSize = (
  trainSize: number | null | undefined,
  testSize: number | null | undefined,
): string =>
  typeof trainSize !== "number" && typeof testSize !== "number"
    ? DASH
    : `${formatNumber(trainSize)} train / ${formatNumber(testSize)} test`

/** Guarded read of the forge brief text (`spec_json.brief.card.description`) — prior art `views/forges/details.tsx`. */
const selectBriefText = (
  specJson: Record<string, unknown> | undefined,
): string | null => {
  const brief = specJson?.brief
  if (brief == null || typeof brief !== "object" || Array.isArray(brief)) {
    return null
  }
  const card = (brief as Record<string, unknown>).card
  if (card == null || typeof card !== "object" || Array.isArray(card)) {
    return null
  }
  const description = (card as Record<string, unknown>).description
  return typeof description === "string" && description.trim() !== ""
    ? description
    : null
}

export default function SetupPanel({
  cycleId,
  defaultOpen = false,
}: SetupPanelProps) {
  const cycleQuery = useEvolutionCycle(cycleId)
  const forgeQuery = useForge(cycleQuery.data?.forge_id)

  const cycle = cycleQuery.data
  const forge = forgeQuery.data
  const budgetInfo = selectBudgetInfo(cycle)
  const cycleConfig = selectCycleConfig(cycle)
  const evaluationProtocol = parseEvaluationProtocol(
    cycle?.forge_spec_snapshot_json,
  )
  const briefText = selectBriefText(forge?.spec_json)

  const objectiveValue = forge?.objective_metric_name ?? DASH

  const compactSummary = [
    `objective ${objectiveValue}`,
    `budget ${budgetInfo.budget != null ? formatCurrency(budgetInfo.budget) : DASH}`,
    `max_iter ${formatNumber(budgetInfo.maxIterations)}`,
    `engine ${cycleConfig?.engine ?? DASH}`,
    `eval ${
      evaluationProtocol.nTrials != null
        ? `×${evaluationProtocol.nTrials} ${evaluationProtocol.aggregation ?? ""}`.trim()
        : DASH
    }`,
  ].join(" · ")

  return (
    <details
      open={defaultOpen || undefined}
      role="region"
      aria-label="Setup — frozen at launch"
      className="group overflow-hidden rounded-[14px] border border-border bg-[#071C20]/50"
    >
      <summary className="flex cursor-pointer list-none select-none items-center gap-3 px-[18px] py-[13px] [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="size-[15px] shrink-0 text-text-tertiary transition-transform duration-200 group-open:rotate-90"
          strokeWidth={2.2}
        />
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-text-tertiary">
          Setup
        </span>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11.5px] text-[#7FA8A0] group-open:hidden">
          {compactSummary}
        </span>
      </summary>

      <div className="grid grid-cols-1 gap-3.5 border-t border-[#0E2E33] p-[18px] md:grid-cols-2">
        <SubPanel title="Forge" kickClassName="text-primary">
          {briefText != null && (
            <p className="mb-1 mt-[3px] text-[12.5px] leading-normal text-[#C6E4DC]">
              “{briefText}”
            </p>
          )}
          <div>
            <KvRow label="objective" valueClassName="text-primary">
              {objectiveValue}
            </KvRow>
            <KvRow label="forge name">{cycle?.forge_name ?? DASH}</KvRow>
            <KvRow label="forge_id" valueClassName="text-[11px]">
              {cycle?.forge_id ?? DASH}
            </KvRow>
            <KvRow label="owner · created" valueClassName="text-[11.5px]">
              {`${forge?.owner_user_id ?? DASH} · ${formatDate(forge?.created_at)}`}
            </KvRow>
            <KvRow label="history" valueClassName="text-[11.5px]">
              {forge
                ? `${forge.n_cycles} cycles · best ${formatScore(forge.best_score)}`
                : DASH}
            </KvRow>
          </div>
        </SubPanel>

        <SubPanel title="Cycle config">
          <div className="mt-[3px]">
            <KvRow label="stopping mode" valueClassName="text-primary">
              {budgetInfo.budget != null
                ? `budget ${formatCurrency(budgetInfo.budget)}`
                : DASH}
            </KvRow>
            <KvRow label="max_iterations">
              {formatNumber(budgetInfo.maxIterations)}
            </KvRow>
            <KvRow label="n_best_agents">
              {formatNumber(budgetInfo.nBestAgents)}
            </KvRow>
            <KvRow label="models" valueClassName="text-[11px]">
              {cycleConfig?.agent_model != null ||
              cycleConfig?.meta_model != null ||
              cycleConfig?.util_model != null
                ? `agent ${cycleConfig?.agent_model ?? DASH} · meta ${
                    cycleConfig?.meta_model ?? DASH
                  } · util ${cycleConfig?.util_model ?? DASH}`
                : DASH}
            </KvRow>
          </div>
        </SubPanel>

        <SubPanel title="Evaluation" kickClassName="text-[#8FE07F]">
          <div className="mt-[3px]">
            <KvRow label="trials">
              {evaluationProtocol.nTrials != null
                ? `×${evaluationProtocol.nTrials}`
                : DASH}
            </KvRow>
            <KvRow label="aggregation">
              {evaluationProtocol.aggregation ?? DASH}
            </KvRow>
            <KvRow label="timeout">
              {evaluationProtocol.timeoutSeconds != null
                ? `${evaluationProtocol.timeoutSeconds}s`
                : DASH}
            </KvRow>
            <KvRow label="dataset">
              {formatDatasetSize(cycle?.train_size, cycle?.test_size)}
            </KvRow>
            <KvRow label="metrics" valueClassName="text-[11px]">
              {forge?.objective_metric_name ?? DASH}
            </KvRow>
          </div>
        </SubPanel>

        <SubPanel
          title="Evolver"
          kickClassName="text-[#B69BFF]"
          className="border-lineage/28"
          headerRight={
            <span className="inline-flex items-center rounded-full border border-lineage/35 bg-lineage/12 px-2 py-0.5 text-[10px] text-[#B69BFF]">
              {cycleConfig?.engine ?? DASH}
            </span>
          }
        >
          {cycleConfig != null && cycleConfig.fields.length > 0 ? (
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              {cycleConfig.fields.map((field) => (
                <StatTile key={field.key} label={field.label}>
                  {formatFieldValue(field)}
                </StatTile>
              ))}
            </div>
          ) : (
            <p className="mt-2.5 text-[12.5px] text-[#7FA8A0]">{DASH}</p>
          )}
        </SubPanel>
      </div>
    </details>
  )
}

const SubPanel = ({
  title,
  children,
  kickClassName = "text-text-tertiary",
  className = "",
  headerRight,
}: {
  title: string
  children: ReactNode
  kickClassName?: string
  className?: string
  headerRight?: ReactNode
}) => (
  <section
    role="group"
    aria-label={title}
    className={`rounded-[14px] border border-border bg-[#041418]/40 px-5 py-[18px] ${className}`}
  >
    {headerRight != null ? (
      <div className="mb-2.5 flex items-center justify-between">
        <span
          className={`font-mono text-[10.5px] uppercase tracking-[0.12em] ${kickClassName}`}
        >
          {title}
        </span>
        {headerRight}
      </div>
    ) : (
      <span
        className={`font-mono text-[10.5px] uppercase tracking-[0.12em] ${kickClassName}`}
      >
        {title}
      </span>
    )}
    {children}
  </section>
)

const KvRow = ({
  label,
  children,
  valueClassName = "",
}: {
  label: string
  children: ReactNode
  valueClassName?: string
}) => (
  <div className="flex items-center justify-between gap-3 border-b border-[#0A2227] py-[7px] last:border-b-0">
    <span className="text-[12.5px] text-[#7FA8A0]">{label}</span>
    <span
      className={`text-right font-mono text-[12.5px] text-foreground ${valueClassName}`}
    >
      {children}
    </span>
  </div>
)

const StatTile = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => (
  <div className="flex flex-col items-start gap-1 rounded-[11px] border border-[#0E2E33] bg-[#041418]/50 px-[11px] py-[9px]">
    <span className="font-mono text-[16px] text-foreground">{children}</span>
    <span className="text-[12.5px] text-[#7FA8A0]">{label}</span>
  </div>
)
